/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified wait time has elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the original function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number,
): (...args: Parameters<T>) => void {
    let timeout: number | undefined;

    return (...args: Parameters<T>): void => {
        const later = (): void => {
            timeout = undefined;
            func(...args);
        };

        window.clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);
    };
}

function roundTo(num: number, dec: number): number {
    const pow = 10 ** dec;
    return Math.round((num + Number.EPSILON) * pow) / pow;
}

export function beauty(num: number): string {
    if (Number.isNaN(num)) {
        return "";
    }

    if (num === 0) {
        return "0";
    }

    const isNegative = num < 0;
    const absNum = Math.abs(num);
    let toReturn = "";
    if (absNum >= 1e9) {
        toReturn = `${roundTo(absNum / 1e9, 2)}B`;
    } else if (absNum >= 1e6) {
        toReturn = `${roundTo(absNum / 1e6, 2)}M`;
    } else if (absNum >= 1e3) {
        toReturn = `${roundTo(absNum / 1e3, 2)}K`;
    } else if (absNum < 1 / 1e3) {
        toReturn = absNum.toExponential();
    } else {
        toReturn = roundTo(absNum, 4).toString();
    }

    return `${isNegative ? "-" : ""}${toReturn}`;
}
